/*
* Copyright (c) 2023 Eugenio Espinoza
*/

import React from 'react';
import './LatestRelease.css';
import melodicoArt from '../../imgs/artists/melodico_img.jpg';
import { artistsData } from '../Artists/shared/artistsData';
import {
  useItunesReleases,
  useItunesTracks,
  formatReleaseDate,
  formatDuration,
} from '../../hooks/useItunesReleases';

const STREAMING_TEMPLATE = [
  { name: 'Spotify', glyph: 'M12 2a10 10 0 1 0 10 10A10 10 0 0 0 12 2Zm4.45 14.5a.72.72 0 0 1-1 .23 11 11 0 0 0-8.28-1.2.72.72 0 1 1-.34-1.39 12.5 12.5 0 0 1 9.38 1.37.72.72 0 0 1 .24.99Zm1.31-2.66a.9.9 0 0 1-1.24.29 14 14 0 0 0-10.54-1.38.9.9 0 1 1-.46-1.74 15.8 15.8 0 0 1 11.94 1.59.9.9 0 0 1 .3 1.24Zm.13-2.77a17 17 0 0 0-12.64-1.51 1.07 1.07 0 1 1-.57-2.06 19.17 19.17 0 0 1 14.26 1.7 1.07 1.07 0 1 1-1.05 1.87Z' },
  { name: 'Apple Music', glyph: 'M16.5 3 8 4.8A1.7 1.7 0 0 0 6.7 6.4v9.8a3 3 0 1 0 1.8 2.8V9.6l7-1.5v6.7a3 3 0 1 0 1.8 2.8V4.7A1.7 1.7 0 0 0 16.5 3Z' },
  { name: 'YouTube Music', glyph: 'M12 2a10 10 0 1 0 10 10A10 10 0 0 0 12 2Zm-1.75 14V8l6 4Z' },
];

/* Fallback used when the iTunes fetch fails or returns nothing — graceful first paint. */
const FALLBACK = {
  title: 'Memorias Nocturnas',
  artist: 'Melódico',
  releaseDate: null,
  trackCount: null,
  artwork: melodicoArt,
  type: 'Long Play',
  appleUrl: 'https://music.apple.com/us/artist/melodico/464785275',
  id: null,
};

function pickArtistMeta(release) {
  if (!release) return null;
  return Object.values(artistsData).find(
    (a) => a.appleArtistId && a.appleArtistId === String(release.artistId)
  );
}

function LatestRelease() {
  const artistIds = Object.values(artistsData)
    .map((a) => a.appleArtistId)
    .filter(Boolean);

  const { latest, loading } = useItunesReleases(artistIds, 15);
  const release = latest || FALLBACK;
  const artistMeta = pickArtistMeta(latest);
  const isLive = Boolean(latest);

  const { tracks } = useItunesTracks(isLive ? release.id : null);

  const socials = (artistMeta && artistMeta.socials) || {};
  const streamingLinks = STREAMING_TEMPLATE.map((p) => {
    let href = '#';
    if (p.name === 'Apple Music') href = release.appleUrl || 'https://music.apple.com/';
    if (p.name === 'Spotify') href = socials.spotify || 'https://open.spotify.com/';
    if (p.name === 'YouTube Music') href = socials.youtube || 'https://music.youtube.com/';
    return { ...p, href };
  });

  return (
    <section id="latest-release" className="mt-release">
      <div className="mt-release__inner mt-container">
        <header className="mt-release__head">
          <span className="mt-eyebrow">
            {isLive ? 'On Rotation · Último estreno' : 'On Rotation · Featured'}
          </span>
          <h2 className="mt-release__title">
            <span>Un nuevo</span>
            <em>capítulo.</em>
          </h2>
          <p className="mt-release__dek">
            El estreno más reciente de la casa — disponible ahora, en todas las
            plataformas.
          </p>
        </header>

        <div className={`mt-release__grid ${loading ? 'is-loading' : ''}`}>
          <div className="mt-release__cover">
            <div className="mt-release__coverInner">
              <img
                src={release.artworkLarge || release.artwork}
                srcSet={release.artworkMedium ? `${release.artworkMedium} 600w, ${release.artworkLarge} 1200w` : undefined}
                alt={`${release.title} artwork`}
              />
              <div className="mt-release__coverShine" aria-hidden="true" />
            </div>
            <div className="mt-release__coverCaption">
              <span>{release.type || 'Release'}</span>
              <span>{release.releaseDate ? formatReleaseDate(release.releaseDate) : 'Streaming'}</span>
            </div>
          </div>

          <div className="mt-release__meta">
            <p className="mt-kicker">Estreno Oficial</p>
            <h3 className="mt-release__album">{release.title}</h3>
            <p className="mt-release__artist">
              una obra de <span>{release.artist}</span>
            </p>

            <p className="mt-release__note">
              Diez piezas grabadas a media luz — sesiones en vivo, decisiones
              lentas, mezclas hechas a mano. Un recordatorio de que la intimidad
              sigue siendo un lenguaje.
            </p>

            {tracks.length > 0 && (
              <ul className="mt-release__tracks">
                {tracks.slice(0, 4).map((t) => (
                  <li key={t.id}>
                    <span>{String(t.number).padStart(2, '0')}</span>
                    {t.name}
                    <em>{formatDuration(t.lengthMs)}</em>
                  </li>
                ))}
              </ul>
            )}

            <div className="mt-release__stream">
              <span className="mt-kicker">Escucha ahora</span>
              <div className="mt-release__streamRow">
                {streamingLinks.map((p) => (
                  <a
                    key={p.name}
                    className="mt-release__streamBtn"
                    href={p.href}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <svg viewBox="0 0 24 24" width="14" height="14" aria-hidden="true">
                      <path d={p.glyph} fill="currentColor" />
                    </svg>
                    <span>{p.name}</span>
                  </a>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

export default LatestRelease;
